'use client';

import { pdf, Document, Page, View, Text, StyleSheet, Image, Font } from '@react-pdf/renderer';

import { LogotypePDF } from '../../../../../../components/logotype/LogotypePDF';
import { APP_PARTNER_LOGOTYPE_URL } from '../../../../../../configs/app.config';
import { LIGHT_PALETTE } from '../../../../../../configs/appearance/theme/palette/lightPalette';
import { checkPartnerName, getPartnerFullName } from '../../../../../../utils/partner/partner.util';
import { availableRelationWithOrganization } from '../../configs/signallerForm.config';

import type { ICreatePDFFileParams, TIsAnonimaParams } from './report.handler.type';

const isAnonim = ({ reporter, address, email, phone }: TIsAnonimaParams) =>
  reporter === '' && address === '' && email === '' && phone[1] === '';

export const createPDFFile = ({
  creationDate,
  reportNumber,
  reporter,
  subject,
  description,
  reportedPersson,
  witness,
  evidence,
  email,
  phone,
  relationWithOrganization,
  address,
  personalDataDisclosure,
  evidenceFiles,
  t,
  availableSubjects,
}: ICreatePDFFileParams): Promise<Blob> => {
  // Register Font
  Font.register({
    family: 'Roboto',
    fonts: [
      {
        src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
        fontWeight: 300,
      },
      {
        src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
        fontWeight: 400,
      },
      {
        src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf',
        fontWeight: 500,
      },
      {
        src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
        fontWeight: 600,
      },
    ],
  });

  // Create style with font-family
  const styles = StyleSheet.create({
    page: {
      fontFamily: 'Roboto',
      fontSize: 12,
      backgroundColor: LIGHT_PALETTE.background.paper,
      padding: 24,
      display: 'flex',
      height: '100%',
    },
    text: { fontWeight: 'bold', flexWrap: 'wrap' },

    header: {
      borderBottom: `1px solid ${LIGHT_PALETTE.grey[200]}`,
      paddingBottom: 16,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    section: {
      borderBottom: `1px solid ${LIGHT_PALETTE.grey[200]}`,
      paddingTop: 16,
      paddingBottom: 16,
    },
    'mb-8': { marginBottom: 8 },
    footer: {
      fontSize: 10,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
      marginTop: 12,
    },
  });

  const anonim = isAnonim({ reporter, address, email, phone });
  const avRelationWithOrganization = availableRelationWithOrganization(t);

  const partnerName = checkPartnerName();

  const MyDoc = (
    <Document language="pl">
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={{ flex: 1, marginRight: 16 }}>
            <Text style={styles.text}>
              {t(`labels.pdfReportTitle.${partnerName}`, {
                company: getPartnerFullName().toUpperCase(),
              })}
            </Text>
            <Text style={styles.text}>{t('labels.pdfReportedOn', { date: creationDate })}</Text>
            <Text style={styles.text}>{t('labels.pdfReportNumber', { reportNumber })}</Text>
          </View>
          <View>
            {APP_PARTNER_LOGOTYPE_URL && (
              <Image
                src={APP_PARTNER_LOGOTYPE_URL}
                style={{
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  maxWidth: 150,
                  maxHeight: 150,
                }}
              />
            )}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.text}>{t('labels.pdfReporter')}</Text>

          {reporter !== '' && <Text>{reporter}</Text>}
          {address !== '' && <Text>{address}</Text>}
          {email !== '' && <Text>{t('labels.pdfEmail', { email })}</Text>}
          {phone[1] !== '' && (
            <Text>{t('labels.pdfPhone', { phone: `${phone[0]} ${phone[1]}` })}</Text>
          )}

          {anonim && <Text>{t('text.pdfAnonymous')}</Text>}

          <Text style={styles.text}>{t(`labels.pdfRelationWithOrganization.${partnerName}`)}</Text>
          <Text>
            {relationWithOrganization
              .map((value) => {
                const label = avRelationWithOrganization.find(
                  ({ value: avValue }) => value === avValue,
                )?.label;

                return label || value;
              })
              .join(', ')}
          </Text>

          <Text style={styles.text}>{t('labels.pdfConsents')}</Text>
          <Text>
            {personalDataDisclosure
              ? t('text.pdfPersonalDataDisclosureAccept')
              : t('text.pdfPersonalDataDisclosureDecline')}
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.text}>{t('labels.pdfSubjectOfReport')}</Text>
          <Text style={styles['mb-8']}>
            {subject
              .map((value) => {
                const label = availableSubjects.find(
                  ({ value: avValue }) => value === avValue,
                )?.label;

                return label || value;
              })
              .join(', ')}
          </Text>

          <Text style={styles.text}>{t('labels.pdfAccusedPerson')}</Text>
          <Text style={styles['mb-8']}>{reportedPersson}</Text>

          <Text style={styles.text}>{t('labels.pdfReportDescription')}</Text>
          <Text style={styles['mb-8']}>{description}</Text>

          <Text style={styles.text}>{t('labels.pdfWitnesses')}</Text>
          <Text style={styles['mb-8']}>{witness}</Text>

          <Text style={styles.text}>{t('labels.pdfEvidence')}</Text>
          <Text style={styles['mb-8']}>{evidence}</Text>

          <Text style={styles.text}>{t('labels.pdfAttachments')}</Text>

          {evidenceFiles.length > 0 ? (
            evidenceFiles.map((fileName) => (
              <Text style={styles['mb-8']} key={fileName}>
                {fileName}
              </Text>
            ))
          ) : (
            <Text style={styles['mb-8']}>{t('labels.missing')}</Text>
          )}
        </View>
        <View style={{ flex: 1 }} />
        <View style={styles.footer} fixed>
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Text>Powered by</Text>
            <View style={{ height: 20, width: 50 }}>
              <LogotypePDF />
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );

  return pdf(MyDoc).toBlob();
};
